import React from 'react'
import { withStyles, makeStyles } from '@material-ui/core/styles'
import {
  Theme,
  Link,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import MuiDialogContent from '@material-ui/core/DialogContent'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import Typography from '@material-ui/core/Typography'
import { FormattedMessage } from 'react-intl'
import messages from '../../../messages'
import { TubeProduct } from '../../../utils/StandardMTOEnum'

const styles = makeStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  rootRow: {
    background: '#F2F3F4',
    fontWeight: theme.typography.fontWeightMedium,
    borderBottom: 0,
    padding: theme.spacing(1),
  },
  tableBody: {
    padding: theme.spacing(1),
  },
  table: {
    minWidth: 850,
  },
  dialog: {
    background: '#F2F3F4',
  },
  icon: {
    fontSize: theme.typography.pxToRem(14),
    verticalAlign: 'middle',
    color: theme.palette.grey[700],
  },
  label: {
    fontWeight: 'normal',
    fontSize: theme.typography.pxToRem(14),
    marginBottom: theme.spacing(2),
  },
  clearButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  resetBorderBottom: {
    borderBottom: '0 none',
    padding: theme.spacing(0),
    paddingTop: theme.spacing(1.25),
  },
  footerText: {
    marginLeft: theme.spacing(2.5),
  },
  styleLink: {
    color: theme.palette.primary.main,
  },
}))

export interface DialogTitleProps {
  id: string
  children: React.ReactNode
  onClose: () => void
}

const DialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props
  const classes = styles()
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.clearButton}
          onClick={onClose}
          color="primary"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  )
}

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent)

interface ExpectedYieldsProps {
  isValid: boolean
  isFullSOligoBackbone: boolean
  productType: string
  linkText?: string
}

const ExpectedYields: React.FC<ExpectedYieldsProps> = (props) => {
  const [open, setOpen] = React.useState(false)
  const { isValid, productType, isFullSOligoBackbone, linkText } = props

  const handleClickOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }
  const classes = styles()
  const NonPhosphorothioatesData = [
    { Scale: '0.025', Desalt: '3/90', Cartridge: 'NA', HPLC: 'NA', Page: 'NA' },
    {
      Scale: '0.05',
      Desalt: '5/150',
      Cartridge: '1/30',
      HPLC: '1/30',
      Page: '0.5/15',
    },
    {
      Scale: '0.2',
      Desalt: '12/360',
      Cartridge: '3/90',
      HPLC: '2.5/75',
      Page: '1/30',
    },
    {
      Scale: '1.0',
      Desalt: '40/1,200',
      Cartridge: '12/360',
      HPLC: '13/390',
      Page: '5/150',
    },
    {
      Scale: '10',
      Desalt: '400/12,000',
      Cartridge: 'NA',
      HPLC: '130/3,900',
      Page: 'NA',
    },
    {
      Scale: '15',
      Desalt: '600/18,000',
      Cartridge: 'NA',
      HPLC: '190/5,700',
      Page: 'NA',
    },
  ]
  const PhosphorothioatesData = [
    {
      Scale: '0.05',
      Desalt: '3.5/105',
      Cartridge: '0.7/21',
      HPLC: '0.7/21',
      Page: '0.35/10.5',
    },
    {
      Scale: '0.2',
      Desalt: '	8.4/252',
      Cartridge: '2.1/63',
      HPLC: '1.75/52.5',
      Page: '0.7/21',
    },
    {
      Scale: '1.0',
      Desalt: '28/840',
      Cartridge: '8.4/252',
      HPLC: '9.1/273',
      Page: '3.5/105',
    },
    {
      Scale: '10',
      Desalt: '280/8,400',
      Cartridge: 'NA',
      HPLC: '91/2,730',
      Page: 'NA',
    },
    {
      Scale: '15',
      Desalt: '420/12,600',
      Cartridge: 'NA',
      HPLC: '133/3,990',
      Page: 'NA',
    },
  ]

  if (productType === TubeProduct.Rna) {
    NonPhosphorothioatesData.splice(0, 1)
  }
  return (
    <div>
      <Link
        component="button"
        underline={isValid ? 'none' : 'hover'}
        disabled={isValid}
        onClick={handleClickOpen}
        className={classes.styleLink}
      >
        {linkText ? (
          linkText
        ) : (
          <i>
            {' '}
            <FormattedMessage {...messages.CONF_YIELDS_TABLE} />{' '}
          </i>
        )}
      </Link>

      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth="md"
      >
        <div className={classes.dialog}>
          <DialogTitle id="customized-dialog-title" onClose={handleClose}>
            <b>
              {productType === TubeProduct.Rna ? (
                <FormattedMessage {...messages.CONF_NON_MODIFIED_RNA_OLIGOS} />
              ) : isFullSOligoBackbone ? (
                <FormattedMessage
                  {...messages._PC_SPEC_NON_MODIFIED_OLIGOS_ST}
                />
              ) : (
                <FormattedMessage {...messages.CONF_NON_MODIFIED_OLIGOS} />
              )}
            </b>
          </DialogTitle>
        </div>

        <DialogContent dividers>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_SCALE} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_DESALT} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_CARTRIDGE} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.HPLC} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_POPUP_PAGE} />}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isFullSOligoBackbone
                ? PhosphorothioatesData.map((data, index) => (
                    <TableRow key={index}>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Scale}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Desalt}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Cartridge}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.HPLC}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Page}
                      </TableCell>
                    </TableRow>
                  ))
                : NonPhosphorothioatesData.map((data, index) => (
                    <TableRow key={index}>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Scale}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Desalt}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Cartridge}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.HPLC}
                      </TableCell>
                      <TableCell align="left" className={classes.tableBody}>
                        {data.Page}
                      </TableCell>
                    </TableRow>
                  ))}
              <TableRow>
                <TableCell className={classes.resetBorderBottom} colSpan={5}>
                  <div className={classes.footerText}>
                    {productType === TubeProduct.Rna ? (
                      <FormattedMessage
                        {...messages.CONF_RNA_EXPECTED_YIELDS_FOOTER_TEXT}
                      />
                    ) : (
                      <FormattedMessage
                        {...messages.CONF_EXPECTED_YIELDS_FOOTER_TEXT}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ExpectedYields
